import { apiError } from "../../../../../utils/formErrors";
import React, { useState } from "react";
import { Box, Stack, Dialog, Select, MenuItem } from '@mui/material';
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import FormField from "../../../../../component/Formfield";
import DataTable from "../../../../../component/DataTable";
import { StatusChip, selectSx } from "../../../../../component/listing";
import { FieldGrid } from "../../../../../component/wizard";
import { requestEncashment, getSelfEncashments } from "../../../../../services/payroll";
import { getSelfLeaveBalance, getAllLeaveType } from "../../../../../services/leave";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens } from "../../../../../theme";
import { money, MONTHS } from "../../admin-main/payroll-new/payrollUtils";

const TONE = { pending: "warning", approved: "success", rejected: "danger" };
const now = new Date();

// Employee-facing: "Encash leave" button + request dialog + own-requests list.
// Lives in the Leave module (balance lives here); payroll only pays it out.
// Standalone requests list (placed in the page body, not the header)
export const EncashmentRequests = ({ requests = [] }) => {
  if (!requests.length) return null;
  return (
    <Box sx={{ mt: "1.5rem" }}>
      <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: "0.75rem", color: tokens.text }}>Encashment requests</Box>
      <DataTable
        rows={requests}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          { key: "leave", label: "Leave", cellSx: { fontWeight: 600 }, render: (e) => e.leaveType?.name || "—" },
          { key: "days", label: "Days", align: "right", render: (e) => e.days },
          { key: "amount", label: "Amount", align: "right", render: (e) => money(e.amount) },
          {
            key: "period",
            label: "Period",
            cellSx: { color: tokens.textMuted },
            render: (e) => (e.month != null ? `${MONTHS[e.month]?.slice(0, 3)} ${e.year}` : "—"),
          },
          { key: "status", label: "Status", render: (e) => <StatusChip label={e.status} tone={TONE[e.status]} /> },
        ]}
      />
    </Box>
  );
};

const EncashLeave = ({ onChanged }) => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [paidTypes, setPaidTypes] = useState([]);
  const [form, setForm] = useState({ leaveType: "", days: "", month: now.getMonth(), year: now.getFullYear(), reason: "" });
  const [saving, setSaving] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    try {
      const [bal, types] = await Promise.all([getSelfLeaveBalance(), getAllLeaveType()]);
      const balByType = {}; (bal?.data || []).forEach((b) => { balByType[String(b.leaveType)] = b.quantity; });
      setPaidTypes((types?.data || []).filter((t) => t.isPaid).map((t) => ({ _id: t._id, name: t.name, quantity: balByType[String(t._id)] || 0 })));
    } catch { /* non-critical */ }
  };

  const submit = async () => {
    if (!form.leaveType || !form.days || Number(form.days) <= 0) { notify.error("Pick a leave type and days"); return; }
    setSaving(true);
    try {
      const res = await requestEncashment({ leaveType: form.leaveType, days: Number(form.days), month: form.month, year: form.year, reason: form.reason.trim() });
      if (res?.success === false) throw apiError(res, "Could not request encashment");
      notify.success("Encashment requested — pending approval");
      setOpen(false); setForm({ leaveType: "", days: "", month: now.getMonth(), year: now.getFullYear(), reason: "" });
      onChanged?.();
    } catch (e) { notify.error(e?.message || "Could not request encashment"); }
    finally { setSaving(false); }
  };

  const selected = paidTypes.find((t) => t._id === form.leaveType);

  return (
    <>
      <BtnSecondary onClick={openDialog} sx={{ borderRadius: "8px" }}>Encash leave</BtnSecondary>

      <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "30rem", maxWidth: "94vw" } }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "0.25rem" }}>Encash leave</Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>Cash out unused paid leave. Paid out via payroll after HR approval.</Box>
        <Box sx={{ mb: "1rem" }}>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Leave type</Box>
          <Select value={form.leaveType} onChange={(e) => setForm((f) => ({ ...f, leaveType: e.target.value }))} size="small" displayEmpty sx={{ ...selectSx, width: "100%" }}>
            <MenuItem value="" disabled>Select paid leave…</MenuItem>
            {paidTypes.map((t) => <MenuItem key={t._id} value={t._id}>{t.name} ({t.quantity} available)</MenuItem>)}
          </Select>
          {selected && <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mt: "0.25rem" }}>{selected.quantity} day(s) available</Box>}
        </Box>
        <FieldGrid>
          <Box>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Days to encash</Box>
            <FormField type="number" value={form.days} onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))} fullwidth noMargin height="2.75rem" />
          </Box>
          <Box>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Pay in</Box>
            <Stack direction="row" spacing={1}>
              <Select value={form.month} onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))} size="small" sx={{ ...selectSx, flex: 1 }}>
                {MONTHS.map((m, i) => <MenuItem key={m} value={i}>{m.slice(0, 3)}</MenuItem>)}
              </Select>
              <Select value={form.year} onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))} size="small" sx={{ ...selectSx, width: "6rem" }}>
                {[now.getFullYear(), now.getFullYear() + 1].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </Select>
            </Stack>
          </Box>
        </FieldGrid>
        <Box sx={{ mt: "1rem" }}>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Reason</Box>
          <FormField type="text" value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} fullwidth noMargin />
        </Box>
        <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "1.5rem" }}>
          <BtnSecondary onClick={() => setOpen(false)} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnPrimary onClick={submit} disabled={saving} sx={{ borderRadius: "8px" }}>{saving ? "Requesting…" : "Request"}</BtnPrimary>
        </Stack>
      </Dialog>
    </>
  );
};

export { getSelfEncashments };
export default EncashLeave;
